import * as amplitude from '@amplitude/analytics-browser';
import Cookie from 'js-cookie';

import type { HTTPError } from '@core/utils/types/errors';

export const getAmplitudeDetails = (): {
  sessionId: number | undefined;
  deviceId: string | undefined;
  deviceFamily: string;
  os: string;
} => {
  const userAgent = navigator.userAgent;
  let deviceFamily = 'Unknown';
  let os = 'Unknown';

  // Detect device family (Amplitude format)
  if (/Android/i.test(userAgent)) {
    deviceFamily = 'Android';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    deviceFamily = 'iOS';
  } else if (/Windows/i.test(userAgent)) {
    deviceFamily = 'Windows';
  } else if (/Mac/i.test(userAgent)) {
    deviceFamily = 'Mac OS X';
  } else if (/Linux/i.test(userAgent)) {
    deviceFamily = 'Linux';
  }

  // Detect OS (browser name and version)
  if (/Chrome/i.test(userAgent)) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Chrome ${version}`;
  } else if (/Firefox/i.test(userAgent)) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Firefox ${version}`;
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    const match = userAgent.match(/Version\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Safari ${version}`;
  } else if (/Edge/i.test(userAgent)) {
    const match = userAgent.match(/Edge\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Edge ${version}`;
  }

  const payload = {
    sessionId: amplitude.getSessionId() || undefined,
    deviceId: amplitude.getDeviceId() || undefined,
    deviceFamily,
    os,
  };

  return payload;
};

async function fetcher<T>(
  url: Parameters<typeof fetch>[0],
  options: Parameters<typeof fetch>[1] & { isFormData?: boolean },
): Promise<T> {
  const { headers = {}, isFormData = false, ...rest } = options || {};

  const amplitudeDetails = getAmplitudeDetails();

  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': Cookie.get('XSRF-TOKEN') ?? '',
    'x-amplitude-details': JSON.stringify(amplitudeDetails),
    ...headers,
  };

  // When sending multipart/form-data, we need to remove the default Content-Type header
  if (isFormData && 'Content-Type' in requestHeaders) {
    delete requestHeaders['Content-Type'];
  }

  const res = await fetch(url, {
    headers: requestHeaders,
    ...rest,
  });

  const contentType = res.headers.get('Content-Type');
  const isJsonResponse = ['application/json', 'application/problem+json'].some(type => contentType?.includes(type));
  const response = isJsonResponse ? await res.json() : await res.text();

  // Handle unsuccessful responses by rejecting the fetch with a general error
  // object augmented with additional metadata about the status and request.
  if (!res.ok) {
    const error: HTTPError = new Error(`An error occurred while fetching "${url}".`);
    error.info = response;
    error.status = res.status;
    error.statusText = res.statusText;
    error.url = url;
    throw error;
  }

  return response as T;
}

export default fetcher;
